import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { useMediaQuery } from 'react-responsive';
import { SelectBox } from '@/component/Form/Select';
import { Button } from '@/component/Form/Button';
import { usePopupWizard } from '@/component/Modal';
import { useIsClientSide } from '@/utils/next';
import { useBedroom, useAddressAutocomplete, useFocusFirstError } from './form';
import { Address } from './Address';
import { ModalFormAdress } from './ModalFormAdress';
import { ModalFormBedrooms } from './ModalFormBedrooms';

export const HomeOwnerNoAddress = ({
  title,
  desc,
  subTitle,
  defaultValues,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const { onFocusError, containerRef: errorContainerRef } =
    useFocusFirstError();
  const [redirecting, setRedirecting] = useState(false);

  const isClient = useIsClientSide();
  const isMobileDevice = useMediaQuery({ query: '(max-width: 640px)' });
  const isMobile = Boolean(isClient && isMobileDevice);
  const {
    currentStep,
    onSetStep,
    onReset: onCloseModal,
  } = usePopupWizard(['address', 'bedrooms']);

  const {
    bedroom,
    options,
    onSelectBedroom,
    onValidateBedroom,
    onBlur: onBedroomBlur,
    isBedroomInvalid,
  } = useBedroom(defaultValues?.home?.numBedrooms);

  const {
    onBlur: onAddressBlur,
    onSelectAddress,
    onResetAddress,
    onAddressChange,
    opened,
    containerRef,
    addresses,
    loading,
    validationError: addressError,
    isAddressInvalid,
    onOpenDropdown,
    address,
    onValidateAddress,
  } = useAddressAutocomplete();

  const handleEstimate = () => {
    setRedirecting(true);
    return router.push({
      pathname: `/onboarding/rental-estimation/${address}`,
      query: {
        bedrooms: bedroom.value,
      },
    });
  };

  const handleSubmit = () => {
    const addrError = onValidateAddress();
    const roomError = onValidateBedroom();
    if (addrError || roomError) {
      requestAnimationFrame(onFocusError);
      return;
    }
    handleEstimate();
  };

  const handleSubmitMobile = () => {
    const addrError = onValidateAddress();
    const roomError = onValidateBedroom();
    if (addrError) {
      return onSetStep('address');
    }
    if (roomError) {
      return onSetStep('bedrooms');
    }
    handleEstimate().then(() => {
      onCloseModal();
    });
  };

  const [displayedAddress, setDisplayedAddress] = useState(
    defaultValues?.searchedAddress
  );

  return (
    <div
      className="container pt-8 pb-[46px] sm:max-lg:pt-[46px] sm:max-lg:pb-[56px] lg:pt-16 lg:pb-[72px] 3xl:rounded-10"
      ref={errorContainerRef}
    >
      <div className="grid lg:grid-cols-2 items-center max-sm:gap-8 sm:max-lg:gap-11 lg:gap-12 lg:px-8">
        <div>
          <h3 className="text-primary-800 text-5xl font-medium leading-[normal]">
            {title}
          </h3>
          <div className="text-slate-700 text-[32px] mt-4 leading-[normal]">
            {displayedAddress || t('form.not-rental.unknown-address')}
          </div>
          <p className="text-slate-500 text-lg font-normal leading-7 mt-6">
            {desc}
          </p>
        </div>
        <div className="flex">
          <div className="flex flex-col justify-between w-full bg-primary-800 rounded-20 py-[46px] px-10">
            <div className="flex flex-col gap-8">
              <div className="text-white text-[32px] font-semibold leading-[normal]">
                {subTitle}
              </div>
              <Address
                containerRef={containerRef}
                isMobile={isMobile}
                isAddressInvalid={isAddressInvalid}
                address={address}
                addressError={addressError}
                onAddressChange={onAddressChange}
                onAddressBlur={onAddressBlur}
                onSetStep={onSetStep}
                onOpenDropdown={onOpenDropdown}
                addresses={addresses}
                disabled={redirecting}
                onSelectAddress={onSelectAddress}
                setDisplayedAddress={setDisplayedAddress}
                loading={loading}
                onResetAddress={onResetAddress}
                opened={opened}
              />
              <div className="w-full">
                <SelectBox
                  options={options}
                  icon="icon-bedroom"
                  label="form.input.bedrooms.label"
                  placeholder="form.input.bedrooms.placeholder"
                  withoutBG
                  error={!isMobile && isBedroomInvalid}
                  onChange={onSelectBedroom}
                  value={bedroom}
                  onClick={isMobile ? () => onSetStep('bedrooms') : undefined}
                  onBlur={onBedroomBlur}
                  dataTestid="inquiryBedrooms"
                  disabled={redirecting}
                  menuIsOpen={isMobile ? false : undefined}
                  onMenuOpen={
                    isMobile ? () => onSetStep('bedrooms') : undefined
                  }
                />
              </div>
            </div>
            <Button
              className="mt-11 self-end"
              onClick={isMobile ? handleSubmitMobile : handleSubmit}
              icon="icon-rocket"
              loading={redirecting}
              dataTestId="inquirySubmit"
              theme="amber"
              size="lg"
            >
              {t('form.home-owner.button')}
            </Button>
          </div>
          {currentStep === 'address' && (
            <ModalFormAdress
              onCloseModal={onCloseModal}
              onSetStep={onSetStep}
              loading={redirecting}
              isAddressInvalid={isAddressInvalid}
              onAddressChange={onAddressChange}
              address={address}
              addresses={addresses}
              onOpenDropdown={onOpenDropdown}
              onAddressBlur={onAddressBlur}
              onResetAddress={onResetAddress}
              onSelectAddress={onSelectAddress}
              setDisplayedAddress={setDisplayedAddress}
              nextStep="bedrooms"
              nextButtonText="form.home-owner.modal-button-next"
              nextButtonIcon="icon-rocket"
              autoFocus={true}
            />
          )}
          {currentStep === 'bedrooms' && (
            <ModalFormBedrooms
              onCloseModal={onCloseModal}
              handleSubmitMobile={handleSubmitMobile}
              loading={redirecting}
              isBedroomInvalid={isBedroomInvalid}
              bedroom={bedroom}
              onBedroomBlur={onBedroomBlur}
              options={options}
              onSelectBedroom={onSelectBedroom}
              nextButtonText="form.home-owner.modal-button-next"
              nextButtonIcon="icon-rocket"
            />
          )}
        </div>
      </div>
    </div>
  );
};
